import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

function AppleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={cn("fill-current", className)}>
      <path d="M16.37 12.42c.03 3.2 2.8 4.26 2.83 4.28-.02.07-.44 1.51-1.45 2.99-.87 1.28-1.78 2.55-3.2 2.58-1.39.03-1.84-.82-3.44-.82-1.59 0-2.1.8-3.41.85-1.37.05-2.41-1.37-3.29-2.64C2.62 17.09 1.2 12.5 3.04 9.31c.91-1.58 2.55-2.58 4.33-2.61 1.35-.03 2.63.91 3.44.91.81 0 2.34-1.13 3.95-.96.67.03 2.56.27 3.78 2.06-.1.06-2.25 1.31-2.17 3.71ZM13.79 4.79c.73-.88 1.22-2.11 1.09-3.33-1.05.04-2.32.7-3.07 1.58-.67.77-1.26 2.01-1.1 3.19 1.17.09 2.35-.6 3.08-1.44Z" />
    </svg>
  );
}

function GoogleWordmark() {
  return (
    <span className="text-[1.65rem] font-semibold tracking-tight">
      <span className="text-[#4285F4]">G</span>
      <span className="text-[#DB4437]">o</span>
      <span className="text-[#F4B400]">o</span>
      <span className="text-[#4285F4]">g</span>
      <span className="text-[#0F9D58]">l</span>
      <span className="text-[#DB4437]">e</span>
    </span>
  );
}

export default function MarketplaceBrandFallback({
  brand,
  className,
  compact = false,
}: {
  brand: string | null | undefined;
  className?: string;
  compact?: boolean;
}) {
  const normalizedBrand = brand?.trim().toLowerCase() ?? "";
  const label = brand?.trim() || "Unknown";

  let content: ReactNode;
  switch (normalizedBrand) {
    case "apple":
      content = <AppleMark className={compact ? "h-12 w-12 text-stone-700" : "h-20 w-20 text-stone-700"} />;
      break;
    case "google":
      content = <GoogleWordmark />;
      break;
    case "samsung":
      content = (
        <span className={cn("font-black uppercase tracking-[0.22em] text-slate-700", compact ? "text-base" : "text-[1.4rem]")}>
          Samsung
        </span>
      );
      break;
    case "sony":
      content = (
        <span className={cn("font-black uppercase tracking-[0.18em] text-slate-800", compact ? "text-base" : "text-[1.45rem]")}>
          Sony
        </span>
      );
      break;
    case "nintendo":
      content = (
        <span
          className={cn(
            "rounded-full border border-rose-300 px-3 py-1 font-bold tracking-wide text-rose-600",
            compact ? "text-sm" : "text-lg"
          )}
        >
          Nintendo
        </span>
      );
      break;
    case "xbox":
    case "microsoft":
      content = (
        <span className={cn("font-black uppercase tracking-[0.18em] text-emerald-700", compact ? "text-base" : "text-[1.35rem]")}>
          Xbox
        </span>
      );
      break;
    case "playstation":
      content = (
        <span className={cn("font-black tracking-tight text-slate-900", compact ? "text-sm" : "text-[1.25rem]")}>
          PlayStation
        </span>
      );
      break;
    case "meta":
      content = (
        <span className={cn("font-semibold tracking-tight text-sky-700", compact ? "text-base" : "text-[1.5rem]")}>
          Meta
        </span>
      );
      break;
    default:
      content = (
        <span className={cn("font-black uppercase tracking-[0.16em] text-slate-700", compact ? "text-sm" : "text-[1.2rem]")}>
          {label}
        </span>
      );
      break;
  }

  return (
    <div
      className={cn(
        "flex h-full w-full items-center justify-center bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(241,245,249,0.96)_100%)]",
        className
      )}
    >
      {content}
    </div>
  );
}
